import React, { useState, useEffect, useContext, useCallback } from 'react';
import { UserGroupIcon } from '@heroicons/react/24/outline';
import { KeycloakContext } from '../App';
import { API_URL } from '../runtimeConfig';
import { useToast } from '../providers/ToastProvider';
import { PageHeader } from '../components/ui/page-header';
import { Skeleton } from '../components/ui/skeleton';

const IAM_URL = API_URL;

interface AdminUser {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  enabled: boolean;
  isAdmin: boolean;
  attributes?: { adminPassword?: string | string[] };
}

interface CreateForm {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  password: string;
}

interface EditForm {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  isAdmin: boolean;
}

const EMPTY_CREATE_FORM: CreateForm = {
  username: '',
  email: '',
  firstName: '',
  lastName: '',
  password: '',
};

const EMPTY_EDIT_FORM: EditForm = {
  email: '',
  firstName: '',
  lastName: '',
  password: '',
  isAdmin: false,
};

export default function AdminUsers() {
  const ctx = useContext(KeycloakContext);
  const keycloak = ctx?.keycloak;
  const { toast } = useToast();

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState<CreateForm>(EMPTY_CREATE_FORM);
  const [creating, setCreating] = useState(false);
  const [loadingUsername, setLoadingUsername] = useState(false);

  // Edit modal
  const [editUser, setEditUser] = useState<AdminUser | null>(null);
  const [editForm, setEditForm] = useState<EditForm>(EMPTY_EDIT_FORM);
  const [editing, setEditing] = useState(false);

  // Delete modal
  const [deleteUser, setDeleteUser] = useState<AdminUser | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Toggle enabled
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const authHeaders = useCallback(
    (): Record<string, string> => ({
      Authorization: `Bearer ${keycloak?.token}`,
      'Content-Type': 'application/json',
    }),
    [keycloak]
  );

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${IAM_URL}/admin/users`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to fetch users');
      const data = await res.json();
      setUsers(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    if (!keycloak?.token) return;
    fetchUsers();
  }, [keycloak?.token, fetchUsers]);

  // ── Create ─────────────────────────────────────────────────

  const openCreate = async () => {
    setCreateForm(EMPTY_CREATE_FORM);
    setShowCreate(true);
    setLoadingUsername(true);
    try {
      const res = await fetch(`${IAM_URL}/admin/users/next-username`, { headers: authHeaders() });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setCreateForm((prev) => ({ ...prev, username: data.username }));
    } catch {
      // silently fail — admin can still type a username
    } finally {
      setLoadingUsername(false);
    }
  };

  const handleCreateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCreateForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await fetch(`${IAM_URL}/admin/users`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(createForm),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((body as any).error || 'Failed to create user');
      setShowCreate(false);
      await fetchUsers();
    } catch (err: any) {
      toast(err.message, 'error');
    } finally {
      setCreating(false);
    }
  };

  // ── Edit ──────────────────────────────────────────────────

  const openEdit = (user: AdminUser) => {
    setEditUser(user);
    const storedPassword = user.attributes?.adminPassword;
    const pw =
      Array.isArray(storedPassword)
        ? (storedPassword[0] ?? '')
        : typeof storedPassword === 'string'
        ? storedPassword
        : '';
    setEditForm({
      email: user.email || '',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      password: pw,
      isAdmin: !!user.isAdmin,
    });
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (e.target instanceof HTMLInputElement && e.target.type === 'checkbox') {
      setEditForm((prev) => ({ ...prev, [name]: e.target instanceof HTMLInputElement ? e.target.checked : false }));
    } else {
      setEditForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editUser) return;
    setEditing(true);
    try {
      // 1. Update profile fields
      const profileRes = await fetch(`${IAM_URL}/admin/users/${editUser.id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          email: editForm.email,
          firstName: editForm.firstName,
          lastName: editForm.lastName,
          password: editForm.password,
        }),
      });
      const profileBody = await profileRes.json().catch(() => ({}));
      if (!profileRes.ok) throw new Error((profileBody as any).error || 'Failed to update user');

      // 2. Update role if changed
      if (!!editUser.isAdmin !== editForm.isAdmin) {
        const roleRes = await fetch(`${IAM_URL}/admin/users/${editUser.id}/role`, {
          method: 'PUT',
          headers: authHeaders(),
          body: JSON.stringify({ isAdmin: editForm.isAdmin }),
        });
        const roleBody = await roleRes.json().catch(() => ({}));
        if (!roleRes.ok) throw new Error((roleBody as any).error || 'Failed to update role');
      }

      setEditUser(null);
      await fetchUsers();
    } catch (err: any) {
      toast(err.message, 'error');
    } finally {
      setEditing(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!deleteUser) return;
    setDeleting(true);
    try {
      const res = await fetch(`${IAM_URL}/admin/users/${deleteUser.id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as any).error || 'Failed to delete user');
      }
      setDeleteUser(null);
      await fetchUsers();
    } catch (err: any) {
      toast(err.message, 'error');
    } finally {
      setDeleting(false);
    }
  };

  // ── Toggle enabled ────────────────────────────────────────

  const handleToggleEnabled = async (user: AdminUser) => {
    setTogglingId(user.id);
    try {
      const res = await fetch(`${IAM_URL}/admin/users/${user.id}/enabled`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ enabled: !user.enabled }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as any).error || 'Failed to update user');
      }
      await fetchUsers();
    } catch (err: any) {
      toast(err.message, 'error');
    } finally {
      setTogglingId(null);
    }
  };

  // ── Render ────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader
          icon={<UserGroupIcon />}
          title="Users"
          description="Create, edit, disable, or delete accounts in Keycloak."
        />
        <div className="rounded-lg border border-border bg-card p-6 space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-destructive/10 p-4">
        <p className="text-sm text-destructive">Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<UserGroupIcon />}
        title="Users"
        description="Create, edit, disable, or delete accounts in Keycloak."
        actions={
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary/90 focus:outline-none focus:ring-2 focus-visible:ring-ring focus:ring-offset-2"
          >
            + Add user
          </button>
        }
      />

      {/* Table */}
      <div className="bg-card shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/30">
              <tr>
                {['Username', 'Email', 'First name', 'Last name', 'User ID', 'Role', 'Status', 'Actions'].map((h) => (
                  <th
                    key={h}
                    className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider last:text-right"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {users.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-center text-sm text-muted-foreground">
                    No users found.
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="hover:bg-muted/40">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-foreground font-mono">
                      {user.username}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {user.email || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {user.firstName || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {user.lastName || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs text-muted-foreground font-mono">
                      {user.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.isAdmin ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted text-foreground">
                          Administrator
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted text-foreground">
                          User
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.enabled ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success/10 text-success">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-destructive/10 text-destructive">
                          Disabled
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-2">
                      {/* Edit */}
                      <button
                        type="button"
                        onClick={() => openEdit(user)}
                        className="inline-flex items-center rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground shadow-sm hover:bg-muted/40 focus:outline-none focus:ring-2 focus-visible:ring-ring focus:ring-offset-2 cursor-pointer"
                      >
                        Edit
                      </button>
                      {/* Toggle enabled */}
                      <button
                        type="button"
                        disabled={togglingId === user.id}
                        onClick={() => handleToggleEnabled(user)}
                        className={`inline-flex items-center rounded-md border px-3 py-1.5 text-xs font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
                          user.enabled
                            ? 'border-amber-300 bg-card text-warning hover:bg-warning/10 focus:ring-amber-400'
                            : 'border-success/40 bg-card text-success hover:bg-success/10 focus:ring-success/40'
                        }`}
                      >
                        {togglingId === user.id ? '...' : user.enabled ? 'Disable' : 'Enable'}
                      </button>
                      {/* Delete */}
                      <button
                        type="button"
                        onClick={() => setDeleteUser(user)}
                        className="inline-flex items-center rounded-md border border-destructive/40 bg-card px-3 py-1.5 text-xs font-medium text-destructive shadow-sm hover:bg-destructive/10 focus:outline-none focus:ring-2 focus:ring-destructive focus:ring-offset-2 cursor-pointer"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Create modal ─────────────────────────────────── */}
      {showCreate && (
        <Modal title="Add new user" onClose={() => setShowCreate(false)}>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground">
                Username
                {loadingUsername && (
                  <span className="ml-2 text-xs text-muted-foreground">Generating…</span>
                )}
              </label>
              <input
                type="text"
                name="username"
                value={createForm.username}
                onChange={handleCreateChange}
                required
                className="mt-1 block w-full rounded-md border-border shadow-sm focus:border-primary focus-visible:ring-ring sm:text-sm font-mono"
                placeholder="e.g. 00007020326"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Auto-generated from the account sequence + today's date. You may override it.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground">First name</label>
                <input
                  type="text"
                  name="firstName"
                  value={createForm.firstName}
                  onChange={handleCreateChange}
                  className="mt-1 block w-full rounded-md border-border shadow-sm focus:border-primary focus-visible:ring-ring sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground">Last name</label>
                <input
                  type="text"
                  name="lastName"
                  value={createForm.lastName}
                  onChange={handleCreateChange}
                  className="mt-1 block w-full rounded-md border-border shadow-sm focus:border-primary focus-visible:ring-ring sm:text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground">Email</label>
              <input
                type="email"
                name="email"
                value={createForm.email}
                onChange={handleCreateChange}
                required
                className="mt-1 block w-full rounded-md border-border shadow-sm focus:border-primary focus-visible:ring-ring sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground">
                Initial password{' '}
                <span className="text-muted-foreground font-normal">(optional)</span>
              </label>
              <input
                type="text"
                name="password"
                value={createForm.password}
                onChange={handleCreateChange}
                className="mt-1 block w-full rounded-md border-border shadow-sm focus:border-primary focus-visible:ring-ring sm:text-sm"
                placeholder="Leave blank for none"
              />
            </div>
            <ModalFooter
              onCancel={() => setShowCreate(false)}
              submitLabel={creating ? 'Creating…' : 'Create user'}
              submitDisabled={creating || loadingUsername}
            />
          </form>
        </Modal>
      )}

      {/* ── Edit modal ───────────────────────────────────── */}
      {editUser && (
        <Modal title={`Edit "${editUser.username}"`} onClose={() => setEditUser(null)}>
          <form onSubmit={handleEdit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground">First name</label>
                <input
                  type="text"
                  name="firstName"
                  value={editForm.firstName}
                  onChange={handleEditChange}
                  className="mt-1 block w-full rounded-md border-border shadow-sm focus:border-primary focus-visible:ring-ring sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground">Last name</label>
                <input
                  type="text"
                  name="lastName"
                  value={editForm.lastName}
                  onChange={handleEditChange}
                  className="mt-1 block w-full rounded-md border-border shadow-sm focus:border-primary focus-visible:ring-ring sm:text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground">Email</label>
              <input
                type="email"
                name="email"
                value={editForm.email}
                onChange={handleEditChange}
                className="mt-1 block w-full rounded-md border-border shadow-sm focus:border-primary focus-visible:ring-ring sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground">Password</label>
              <input
                type="text"
                name="password"
                value={editForm.password}
                onChange={handleEditChange}
                className="mt-1 block w-full rounded-md border-border shadow-sm focus:border-primary focus-visible:ring-ring sm:text-sm font-mono"
                placeholder="Enter new password or leave unchanged"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Changing this resets the Keycloak login credential. Leave blank to keep current password.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground">Role</label>
              <select
                name="isAdmin"
                value={editForm.isAdmin ? 'admin' : 'user'}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, isAdmin: e.target.value === 'admin' }))
                }
                className="mt-1 block w-full rounded-md border-border shadow-sm focus:border-primary focus-visible:ring-ring sm:text-sm"
              >
                <option value="user">User</option>
                <option value="admin">Administrator</option>
              </select>
            </div>
            <ModalFooter
              onCancel={() => setEditUser(null)}
              submitLabel={editing ? 'Saving…' : 'Save changes'}
              submitDisabled={editing}
            />
          </form>
        </Modal>
      )}

      {/* ── Delete confirm modal ─────────────────────────── */}
      {deleteUser && (
        <Modal title="Delete user" onClose={() => setDeleteUser(null)}>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to permanently delete{' '}
            <span className="font-semibold font-mono">{deleteUser.username}</span>
            {deleteUser.email ? ` (${deleteUser.email})` : ''}? This cannot be undone.
          </p>
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setDeleteUser(null)}
              disabled={deleting}
              className="rounded-md border border-border bg-card px-4 py-2 text-sm font-medium text-foreground hover:bg-muted/40 disabled:opacity-50 cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              className="rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 cursor-pointer"
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Shared sub-components ──────────────────────────────────────────────────

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-background/70 backdrop-blur-sm transition-opacity"
          aria-hidden="true"
          onClick={onClose}
        />
        <div className="inline-block align-bottom bg-card rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-card px-6 pt-5 pb-4">
            <div className="flex items-start justify-between mb-5">
              <h3 className="text-lg font-semibold text-foreground">{title}</h3>
              <button
                type="button"
                onClick={onClose}
                className="text-muted-foreground hover:text-muted-foreground focus:outline-none cursor-pointer"
              >
                <span className="sr-only">Close</span>
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

function ModalFooter({ onCancel, submitLabel, submitDisabled }: { onCancel: () => void; submitLabel: string; submitDisabled: boolean }) {
  return (
    <div className="mt-6 flex justify-end gap-3">
      <button
        type="button"
        onClick={onCancel}
        disabled={submitDisabled}
        className="rounded-md border border-border bg-card px-4 py-2 text-sm font-medium text-foreground hover:bg-muted/40 disabled:opacity-50 cursor-pointer"
      >
        Cancel
      </button>
      <button
        type="submit"
        disabled={submitDisabled}
        className="rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50 cursor-pointer"
      >
        {submitLabel}
      </button>
    </div>
  );
}
